import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	HostBinding,
	Input,
	OnInit,
	Output,
	ViewChild,
	ViewEncapsulation,
} from "@angular/core";
import { MatLegacyDialog as MatDialog } from "@angular/material/legacy-dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { select, Store } from "@ngrx/store";
import { GtmEventAction, GtmLobbyName } from "@obg/gtm.types/adaptive";
import { LOGIN_CONSENTS_DIALOG_ID } from "@obg/mat.ui.common";
import { LATEST_TC_DIALOG_ID, UPDATED_TC_DIALOG_ID } from "@obg/mat.ui.common/terms-and-conditions";
import { DialogLayoutComponent } from "@obg/mat.ui.core";
import { LOGIN_DIALOG_ID } from "@obg/ngx.common";
import {
	AUTH_MESSAGE_CODES,
	AUTH_MESSAGE_CONTEXT,
	CORE_ROUTE,
	CoreState,
	Jurisdiction,
	JurisdictionSelector,
	MessageService,
	MessageType,
	SessionConfigService,
} from "@obg/ngx.core";
import { TranslationGroup, TranslationSelector } from "@obg/ngx.core.content";
import { coerceBoolean, PlatformService, TelemetryService } from "@obg/ngx.global.evo";
import { RouteAction } from "@obg/ngx.router";
import { Observable, Subscription, timer } from "rxjs";
import { filter, map, take, tap, withLatestFrom } from "rxjs/operators";

import { LoginFormService } from "../../login-form.service";
import { LoginUiFlowService } from "../../login-ui/login-ui-flow.service";
import { SITE_LOGIN_TRANSLATIONS } from "../../login.model";
import { LOGIN_FLOW_DIALOG_ID } from "../../new-login-flow/containers/login-flow/login-flow.dialog";
import { NewFlowService } from "../../new-login-flow/new-flow.service";

import { LOGIN_DIALOG_CONTENT } from "./login-with-dialog.const";

@Component({
	selector: "obg-m-login-with-dialog",
	templateUrl: "./login-with-dialog.container.html",
	styleUrls: ["./login-with-dialog.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class LoginWithDialogContainer implements OnInit {
	@HostBinding("class.obg-m-login-with-dialog") hostCssClass = true;

	@Input()
	@coerceBoolean()
	allLinksExternal = false;

	@Input()
	@coerceBoolean()
	hideFooter = false;

	@Output() loginSuccess = new EventEmitter();
	@Output() busy = new EventEmitter<boolean>();

	@ViewChild("dialog", { static: true }) dialogLayout: DialogLayoutComponent | undefined;

	dialogId = LOGIN_DIALOG_ID;
	sgaView = false;
	isJurisdictionDga$: Observable<boolean> | undefined;
	closeDialogAfterLogin$: Subscription | undefined;
	modalTitle$: Observable<string> | undefined;
	descriptionText$: Observable<string> | undefined;
	defaultKey = LOGIN_DIALOG_CONTENT.loginKey;
	registerDescriptionKey = LOGIN_DIALOG_CONTENT.registerDescriptionKey;
	translations$: Observable<Record<string, string | undefined>> | undefined;
	isLoginMfeEnable = this.loginFormService.mfeLoginFlag();
	checked: boolean | undefined;
	error: string | undefined;
	customerCanLogInAt: string | undefined;
	invalidCredentials$: Subscription | undefined;
	sessionLimitExpired$: Subscription | undefined;
	disallowLogin = false;

	constructor(
		private store: Store<CoreState>,
		private route: ActivatedRoute,
		private router: Router,
		private matDialog: MatDialog,
		private platformService: PlatformService,
		private loginFormService: LoginFormService,
		private translationSelector: TranslationSelector,
		private messageService: MessageService,
		private changeDetector: ChangeDetectorRef,
		private loginUiFlowService: LoginUiFlowService,
		private telemetryService: TelemetryService,
		private sessionConfigService: SessionConfigService,
		private newFlowService: NewFlowService
	) {}

	ngOnInit() {
		this.disallowLogin = this.sessionConfigService.isCountryBlockingEnabled();
		this.telemetryService.trackVirtualPageView({
			Lobby: false,
			LobbyName: GtmLobbyName.Login,
			VirtualUrl: "/login/",
			VirtualTitle: GtmLobbyName.Login,
			event: GtmEventAction.VirtualPageView,
		});

		// if accept consents or accept terms and conditions dialog is open, login dialog should not open
		const dialogIds = [LOGIN_CONSENTS_DIALOG_ID, UPDATED_TC_DIALOG_ID, LATEST_TC_DIALOG_ID, LOGIN_FLOW_DIALOG_ID];
		if (this.matDialog.openDialogs?.find((dialog) => dialogIds.includes(dialog.id))) {
			this.platformService
				.forClient(() => timer(0))
				.pipe(take(1))
				// eslint-disable-next-line rxjs/no-ignored-subscription
				.subscribe(() => this.dialogLayout?.close());
			return;
		}

		this.modalTitle$ = this.route.queryParams.pipe(
			filter((param) => !!param.isRegister),
			withLatestFrom(this.store.select(JurisdictionSelector.get)),
			map(([, jurisdiction]) => {
				this.router.navigate([], {
					queryParams: {
						isRegister: null,
					},
					queryParamsHandling: "merge",
				});
				return jurisdiction === Jurisdiction.Dga
					? LOGIN_DIALOG_CONTENT.dgaRegisterKey
					: LOGIN_DIALOG_CONTENT.registerKey;
			})
		);

		this.descriptionText$ = this.route.queryParams.pipe(
			withLatestFrom(this.store.select(JurisdictionSelector.get)),
			filter(([param, jurisdiction]) => !!param.isRegister && jurisdiction === Jurisdiction.Dga),
			map(() => this.registerDescriptionKey)
		);

		this.isJurisdictionDga$ = this.store.pipe(
			select(JurisdictionSelector.get),
			map((jurisdiction) => jurisdiction === Jurisdiction.Dga)
		);

		this.closeDialogAfterLogin$ = this.newFlowService.closeLoginModal$
			.pipe(tap(() => this.closeDialog()))
			.subscribe();

		this.translations$ = this.store.pipe(
			this.translationSelector.getByKeys_v2$(SITE_LOGIN_TRANSLATIONS.translations, [TranslationGroup.Common]),
			map((keys) => Object.fromEntries(keys.map((_, i) => [SITE_LOGIN_TRANSLATIONS.translations[i], keys[i]])))
		);
		this.fabricErrors();
	}

	closeDialog() {
		if (this.dialogLayout) {
			this.dialogLayout.close();
			this.closeDialogAfterLogin$?.unsubscribe();
			this.invalidCredentials$?.unsubscribe();
			this.sessionLimitExpired$?.unsubscribe();
		} else {
			this.matDialog.closeAll();
		}
	}
	rememberMeToggle(checked: boolean) {
		this.checked = checked;
	}

	routeLinks(link: string) {
		this.closeDialog();
		if (link === "registration") {
			this.store.dispatch(RouteAction.navigate(CORE_ROUTE.registration.register));
		} else if (link === "forgetPassword") {
			this.store.dispatch(RouteAction.navigate(CORE_ROUTE.auth.forgotPassword));
		}
	}

	loginValues(form: any) {
		this.error = undefined;
		if (form.username === "" || form.password === "") {
			this.error = `login.${AUTH_MESSAGE_CODES.login.invalidCredentials.toLowerCase()}`;
			return;
		}
		this.loginFormService.loginFabric(form);
	}

	// The handle of errors will be part of the Login Migration SDK in a service
	// Those 2 errors has been done in the component as a temporary solution
	fabricErrors() {
		this.invalidCredentials$ = this.messageService
			.consume$({
				context: AUTH_MESSAGE_CONTEXT.login,
				messageType: MessageType.Error,
				messageCode: AUTH_MESSAGE_CODES.login.invalidCredentials,
			})
			.pipe(
				filter((message) => !!message.length),
				tap((message) => {
					this.busy.emit(false);
					this.error = `login.${message[0].messageCode.toLowerCase()}`;
				})
			)
			.subscribe(() => this.changeDetector.markForCheck());

		this.sessionLimitExpired$ = this.loginUiFlowService
			.sessionLimitExpired$()
			.pipe(
				tap((error) => {
					this.busy.emit(false);
					this.customerCanLogInAt = error.customerCanLogInAt;
					this.error = `login.${error?.code}`.toLowerCase();
				})
			)
			.subscribe(() => this.changeDetector.markForCheck());
	}
}
